import React, { useState } from "react";
import { Link } from "react-router-dom";
import { createCheckoutSession } from "../api/subscriptionClient";

function CheckItem({ children }) {
    return (
        <div className="flex items-start gap-2 text-sm text-gray-700">
            <svg className="w-4 h-4 text-accent-500 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
            </svg>
            <span>{children}</span>
        </div>
    );
}

export default function SubscriptionRequired() {
    const [pendingPlan, setPendingPlan] = useState(null);
    const [error, setError] = useState("");

    const startCheckout = async (plan, withTrial) => {
        setError("");
        setPendingPlan(`${plan}-${withTrial ? "trial" : "now"}`);

        try {
            const { checkoutUrl } = await createCheckoutSession({ plan, withTrial });
            window.location.href = checkoutUrl;
        } catch (err) {
            setError(err.response?.data?.message || "Failed to create checkout session");
            setPendingPlan(null);
        }
    };

    const isLoading = (key) => pendingPlan === key;
    const anyLoading = pendingPlan !== null;

    return (
        <div className="max-w-3xl mx-auto">
            {/* Account management links */}
            <div className="mb-6 p-4 bg-gray-50 border border-gray-200 rounded-lg">
                <p className="text-sm text-gray-500 mb-3">
                    You can still manage your account while unsubscribed:
                </p>
                <div className="flex flex-wrap gap-3">
                    <Link to="/settings" className="text-sm text-accent-600 hover:text-accent-700 transition-colors">
                        Settings
                    </Link>
                    <span className="text-gray-400">|</span>
                    <Link to="/profile" className="text-sm text-accent-600 hover:text-accent-700 transition-colors">
                        Profile
                    </Link>
                    <span className="text-gray-400">|</span>
                    <Link to="/accounts" className="text-sm text-accent-600 hover:text-accent-700 transition-colors">
                        Linked accounts
                    </Link>
                </div>
            </div>

            <div className="text-center mb-8">
                <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 tracking-tight mb-2">
                    Start building wealth with OmniWorth
                </h1>
                <p className="text-gray-600">
                    14-day free trial
                </p>
            </div>

            {error && (
                <div className="mb-6 p-3 bg-red-50 border border-red-200 rounded-lg">
                    <p className="text-sm text-danger-500">{error}</p>
                </div>
            )}

            {/* Pricing cards */}
            <div className="grid md:grid-cols-2 gap-4 mb-8 items-stretch">
                {/* Monthly */}
                <div className="card p-8 flex flex-col">
                    <div className="text-sm font-medium text-gray-500 mb-1">Monthly</div>
                    <div className="flex items-baseline gap-1 mb-1">
                        <span className="text-4xl font-semibold text-gray-900">$12</span>
                        <span className="text-gray-500">/month</span>
                    </div>
                    <div className="text-xs text-gray-500 mb-6">Month to month</div>

                    <div className="mt-auto">
                        <button
                            onClick={() => startCheckout("monthly", true)}
                            disabled={anyLoading}
                            className="btn-secondary w-full py-3 text-base mb-2"
                        >
                            {isLoading("monthly-trial") ? "Redirecting…" : "Start free trial"}
                        </button>
                        <button
                            onClick={() => startCheckout("monthly", false)}
                            disabled={anyLoading}
                            className="w-full text-xs text-gray-500 hover:text-accent-600 transition-colors py-1"
                        >
                            {isLoading("monthly-now") ? "Redirecting…" : "Or subscribe now"}
                        </button>
                        <p className="mt-3 text-xs text-gray-500 text-center">Billed monthly. Cancel anytime.</p>
                    </div>
                </div>

                {/* Annual */}
                <div className="card p-8 flex flex-col ring-2 ring-accent-500 ring-offset-0 relative">
                    <div className="absolute -top-3 right-6 bg-success-500 text-white text-xs font-medium px-2.5 py-1 rounded-full">
                        Save 31%
                    </div>
                    <div className="text-sm font-medium text-gray-500 mb-1">Annual</div>
                    <div className="flex items-baseline gap-1 mb-1">
                        <span className="text-4xl font-semibold text-gray-900">$99</span>
                        <span className="text-gray-500">/year</span>
                    </div>
                    <div className="text-xs text-gray-500 mb-6">$8.25/mo effective</div>

                    <div className="mt-auto">
                        <button
                            onClick={() => startCheckout("annual", true)}
                            disabled={anyLoading}
                            className="btn-primary w-full py-3 text-base mb-2"
                        >
                            {isLoading("annual-trial") ? "Redirecting…" : "Start free trial"}
                        </button>
                        <button
                            onClick={() => startCheckout("annual", false)}
                            disabled={anyLoading}
                            className="w-full text-xs text-gray-500 hover:text-accent-600 transition-colors py-1"
                        >
                            {isLoading("annual-now") ? "Redirecting…" : "Or subscribe now"}
                        </button>
                        <p className="mt-3 text-xs text-gray-500 text-center">Billed annually. Cancel anytime.</p>
                    </div>
                </div>
            </div>

            {/* Features */}
            <div className="card p-6 sm:p-8">
                <h2 className="text-sm font-semibold text-gray-900 uppercase tracking-wider mb-4">
                    What's included
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3">
                    <CheckItem>Unlimited linked accounts</CheckItem>
                    <CheckItem>Net worth, FI number, savings rate</CheckItem>
                    <CheckItem>Monte Carlo projections</CheckItem>
                    <CheckItem>What-if scenario modeling</CheckItem>
                    <CheckItem>Goals with auto-tracking</CheckItem>
                    <CheckItem>Spending breakdown by category</CheckItem>
                    <CheckItem>Investment portfolio breakdown</CheckItem>
                    <CheckItem>Automatic daily data sync</CheckItem>
                </div>
            </div>

            <p className="text-xs text-gray-500 text-center mt-6">
                Secure payment powered by Stripe.
            </p>
        </div>
    );
}
